import { ContractReadError, contractReadError } from "../contracts/contract-errors";
import { walletErrorCode } from "./wallet-errors";

// Keep attribution out of thrown values: callers rely on original error types
// (including ContractReadError) and cancellation identity. Nothing is persisted.
const failedSteps = new WeakMap<object, string>();
let nextTrace = 0;

export function createRedemptionDiagnostics(signal?: AbortSignal) {
  const trace = ++nextTrace;
  return {
    async step<T>(step: string, action: () => Promise<T>): Promise<T> {
      const started = performance.now();
      let status = "ok";
      try { return await action(); }
      catch (error) {
        status = signal?.aborted ? "cancelled" : "error";
        if (error && typeof error === "object" && !failedSteps.has(error)) failedSteps.set(error, step);
        throw error;
      } finally {
        // Metadata only: never log wallet identity, RPC params, results or errors.
        try {
          console.debug("[redemption]", { trace, step, status,
            durationMs: Math.round((performance.now() - started) * 10) / 10 });
        } catch { /* Diagnostics must never change the operation outcome. */ }
      }
    },
  };
}

export type RedemptionDiagnostics = ReturnType<typeof createRedemptionDiagnostics>;

/** Presentation only: decode known reverts and retain plain/nested RPC messages. */
export function redemptionError(error: unknown, fallback: string): string {
  const decoded = error instanceof ContractReadError ? error : contractReadError(error);
  const messages: string[] = [];
  const queue: unknown[] = [error], seen = new Set<unknown>();
  while (queue.length && seen.size < 12) {
    const value = queue.shift();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    if (typeof value === "string" && value.trim() && !/^0x[0-9a-f]+$/i.test(value)) messages.push(value.trim());
    if (typeof value !== "object") continue;
    const item = value as Record<string, unknown>;
    if (typeof item.message === "string" && item.message.trim()) messages.push(item.message.trim());
    queue.push(item.cause, item.error, item.originalError, item.data);
  }
  const message = decoded instanceof ContractReadError ? decoded.message : [...new Set(messages)].slice(0, 3).join(" · ") || fallback;
  const code = walletErrorCode(error);
  const step = error && typeof error === "object" ? failedSteps.get(error) : undefined;
  return `${step ? `${step}: ` : ""}${message}${code === undefined ? "" : ` (RPC code ${code})`}`;
}
